import React, { useEffect, useRef, useState } from 'react'
import { Droppable } from 'react-beautiful-dnd'
import { ChevronDown, ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react'
import { Button } from 'components/ui'
import { useCreationText } from 'components/TasksV2/TaskCreation/hooks/useCreationText'
import { useSubtaskMenu } from 'components/TasksV2/TaskCreation/hooks/useSubtaskMenu'
import { subtaskTypeKeys } from 'components/TasksV2/TaskCreation/model/subtaskTypes'
import styles from './styles.module.css'

interface ICategoryGroupProps {
  id: string
  name: string
  indices: number[]
  active: boolean
  readOnly?: boolean
  busy: boolean
  rtl: boolean
  buttons: ReturnType<typeof useSubtaskMenu>['buttons']
  renderItem: (index: number, dragIndex?: number) => React.ReactNode
  onActivate: () => void
  onAdd: (type: number) => void
  onRemove: () => void
  removeLabel: string
}

export function CategoryGroup({
  id,
  name,
  indices,
  active,
  busy,
  readOnly = false,
  rtl,
  buttons,
  renderItem,
  onActivate,
  onAdd,
  onRemove,
  removeLabel,
}: ICategoryGroupProps) {
  const t = useCreationText()
  const [collapsed, setCollapsed] = useState(false)
  const [choosingType, setChoosingType] = useState(false)
  const previousCount = useRef(indices.length)

  useEffect(() => {
    if (indices.length > previousCount.current) {
      setCollapsed(false)
      setChoosingType(false)
    }
    previousCount.current = indices.length
  }, [indices.length])

  return (
    <section className={styles.group} data-active={active} aria-label={name}>
      <div className={styles.heading}>
        <button
          type="button"
          aria-expanded={!collapsed}
          aria-controls={`category-${id}`}
          disabled={busy}
          onClick={() => {
            onActivate()
            setCollapsed((value) => !value)
          }}
        >
          <span>
            <strong dir="auto">{name}</strong>
            {!collapsed && <small>{indices.length ? `${indices.length} ${t.subtasks}` : t.noSubtasks}</small>}
          </span>
          {collapsed ? rtl ? <ChevronLeft size={18} /> : <ChevronRight size={18} /> : <ChevronDown size={18} />}
        </button>
        {!readOnly && (
          <Button
            type="button"
            variant="icon"
            aria-label={removeLabel}
            isDisabled={busy}
            color="#dc5656"
            onClick={onRemove}
          >
            <Trash2 size={16} />
          </Button>
        )}
      </div>
      {!collapsed && (
        <div id={`category-${id}`}>
          <Droppable droppableId={`category:${id}`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className={styles.items}>
                {indices.map((index, dragIndex) => renderItem(index, dragIndex))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
          {!readOnly && (
            <div className={styles.add}>
              {choosingType && active ? (
                <div className={styles.types}>
                  {buttons.map(({ type, Component }) => (
                    <Button
                      type="button"
                      key={type}
                      variant="secondary"
                      isDisabled={busy}
                      startIcon={<Component width="30px" height="30px" />}
                      onClick={() => {
                        onAdd(type)
                        setChoosingType(false)
                      }}
                    >
                      {t[subtaskTypeKeys[type]]}
                    </Button>
                  ))}
                  <Button type="button" variant="ghost" isDisabled={busy} onClick={() => setChoosingType(false)}>
                    {t.cancel}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  isDisabled={busy}
                  startIcon={<Plus size={16} />}
                  aria-label={`${t.add} — ${name}`}
                  onClick={() => {
                    onActivate()
                    setChoosingType(true)
                  }}
                >
                  {t.add}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
